import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getResellerDashboard } from '@/modules/Reseller/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import Paper from '@/components/Paper'
import { Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'
import ChartIcons from '@/icons/ChartIcons'
import UserGroupIcons from '@/icons/UserGroupIcons'
import ServiceIcons from '@/icons/ServiceIcons'
import BadgeDollarIcons from '@/icons/BadgeDollarIcons'
import StarIcons from '@/icons/StarIcons'
import GokuDataGrid from '@/components/GokuDataGrid'
import { Title, Typogaphy } from 'vnetwork-ui'
import { GridColDef } from '@mui/x-data-grid'
import { TopReseller } from '@/modules/Reseller/types'
import CellStatus from '@/components/GokuDataGrid/CellStatus'
import { RESELLER_TIER_OPTIONS } from '@/modules/Reseller/constants'
import { ResponseArray } from '@/types/shared'

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        maximumFractionDigits: 0,
    }).format(value)
}

const formatNumber = (value: number) => {
    return new Intl.NumberFormat('vi-VN').format(value)
}

interface StatCardProps {
    label: string
    value: string | number
    icon?: React.ReactNode
    className?: string
    loading?: boolean
}

const StatCard: React.FC<StatCardProps> = ({
    label,
    value,
    icon,
    className = '',
    loading,
}) => {
    if (loading) {
        return (
            <div className={`p-4 ${className}`}>
                <Skeleton width={80} height={20} />
                <Skeleton width={120} height={32} className="mt-2" />
            </div>
        )
    }

    return (
        <div className={`p-4 ${className}`}>
            <div className="flex items-center gap-2">
                {icon && <div className="w-5 h-5 text-gray-400">{icon}</div>}
                <p className="text-sm text-gray-500">{label}</p>
            </div>
            <p className="text-2xl font-bold mt-2">{value}</p>
        </div>
    )
}

const DashboardStats = () => {
    const { t } = useTranslation('reseller')
    const navigate = useNavigate()

    const { data, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.RESELLERS, 'dashboard'],
        queryFn: getResellerDashboard,
        staleTime: 60000, // 1 minute
    })

    const stats = data?.result
    const topResellers = stats?.top_resellers || []

    const columns: GridColDef<TopReseller & { rank: number }>[] = [
        {
            field: 'rank',
            headerName: t('rank'),
            width: 60,
            sortable: false,
            renderCell: ({ row }) => (
                <Typogaphy className="font-medium">{row.rank}</Typogaphy>
            ),
        },
        {
            field: 'reseller_name',
            headerName: t('reseller_name'),
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: ({ row }) => (
                <Typogaphy className="font-medium">
                    {row.reseller_name}
                </Typogaphy>
            ),
        },
        {
            field: 'reseller_tier',
            headerName: t('tier'),
            width: 120,
            sortable: false,
            renderCell: ({ row }) => {
                const tier = RESELLER_TIER_OPTIONS(t)?.find(
                    (item) => item?.value === row.reseller_tier
                )

                return (
                    <CellStatus
                        label={tier?.label || row.reseller_tier || '-'}
                        color={tier?.color}
                        className="capitalize"
                    />
                )
            },
        },
        {
            field: 'customer_count',
            headerName: t('customers'),
            width: 120,
            sortable: false,
            align: 'right',
            headerAlign: 'right',
            renderCell: ({ row }) => (
                <Typogaphy className="font-semibold">
                    {formatNumber(row.customer_count)}
                </Typogaphy>
            ),
        },
    ]

    // Add rank to each reseller
    const topResellersWithRank = topResellers.map((reseller, index) => ({
        ...reseller,
        rank: index + 1,
    }))

    const apiCall = async (): Promise<
        ResponseArray<TopReseller & { rank: number }>
    > => ({
        results: topResellersWithRank,
        pagination: {
            total: topResellersWithRank.length,
            page: 1,
            page_size: topResellersWithRank.length,
            total_pages: 1,
            page_next: 1,
            page_prev: 1,
        },
    })

    return (
        <div className="space-y-4 mt-4 mb-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Paper>
                    <StatCard
                        label={t('total_resellers')}
                        value={formatNumber(stats?.total_resellers || 0)}
                        icon={<ChartIcons />}
                        loading={isLoading}
                    />
                </Paper>
                <Paper>
                    <StatCard
                        label={t('total_customers')}
                        value={formatNumber(stats?.total_customers || 0)}
                        icon={<UserGroupIcons />}
                        loading={isLoading}
                    />
                </Paper>
                <Paper>
                    <StatCard
                        label={t('active_services')}
                        value={formatNumber(stats?.total_active_services || 0)}
                        icon={<ServiceIcons />}
                        loading={isLoading}
                    />
                </Paper>
                <Paper>
                    <StatCard
                        label={t('monthly_recurring')}
                        value={formatCurrency(stats?.total_mrr || 0)}
                        icon={<BadgeDollarIcons />}
                        loading={isLoading}
                        className="text-blue-600"
                    />
                </Paper>
                <Paper>
                    <StatCard
                        label={t('total_revenue')}
                        value={formatCurrency(stats?.total_revenue || 0)}
                        icon={<BadgeDollarIcons />}
                        loading={isLoading}
                        className="text-green-600"
                    />
                </Paper>
            </div>

            {/* Top Resellers */}
            {topResellers.length > 0 && (
                <GokuDataGrid
                    columns={columns}
                    reactQueryKey={`${QUERY_KEYS.RESELLERS}-dashboard-top-resellers`}
                    apiCall={apiCall}
                    paginationMode="client"
                    options={{
                        title: (
                            <Title
                                title={t('top_resellers')}
                                icon={() => (
                                    <StarIcons className="text-yellow-500 w-6 h-6" />
                                )}
                            />
                        ),
                        showHideColumn: false,
                        showSearch: false,
                        showCustomPagination: false,
                    }}
                    dataGridProps={{
                        getRowId: (row) => row.reseller_id,
                        onRowClick: (params) => {
                            navigate(
                                generatePathDatacentral(paths.resellerDetail, {
                                    id: params.row.reseller_id.toString(),
                                })
                            )
                        },
                        sx: {
                            '& .MuiDataGrid-row': {
                                cursor: 'pointer',
                            },
                        },
                    }}
                />
            )}
        </div>
    )
}

export default DashboardStats
